/**
 * El traductor: una clave entra, un texto sale.
 *
 * ── Por qué claves con nombre y no el español como clave ────────────────────
 * Usar el texto original como clave se escribe solo el primer día y se vuelve
 * ingobernable el segundo: cambiar una coma rompe su traducción en silencio, y
 * no hay forma de preguntar qué falta por traducir. Con `general.name.label` el
 * texto se puede corregir sin tocar nada más, y lo que falta se puede listar.
 *
 * ── Por qué no una librería ─────────────────────────────────────────────────
 * Dos idiomas, sin plurales eslavos ni formatos de género. Lo que hace falta
 * cabe aquí y no ata el proyecto a nada; el día que entre un idioma con reglas
 * raras, migrar a `i18next` es mecánico porque la forma de las claves es la
 * misma.
 */
import { DEFAULT_LANGUAGE } from './language';
/**
 * Rellena los huecos. `{nombre}` y no `%s` ni `{0}`: quien traduce necesita
 * poder mover el hueco de sitio —el orden de las palabras cambia entre
 * idiomas— y para eso tiene que saber cuál es cada uno.
 */
export function interpolate(template, vars) {
    if (!vars)
        return template;
    return template.replace(/\{(\w+)\}/g, (match, name) => {
        const value = vars[name];
        /* Un hueco sin valor se deja tal cual en vez de escribir «undefined»: así
           se ve qué falta y no se publica una frase rota. */
        return value === undefined ? match : String(value);
    });
}
/**
 * El traductor de un idioma.
 *
 * Cuando falta una clave cae al idioma por defecto, y si tampoco está devuelve
 * LA CLAVE. Nunca una cadena vacía: un hueco en blanco en la pantalla no dice
 * qué pasó, y `general.name.label` en mitad de un formulario se ve, se busca y
 * se arregla en un minuto.
 */
export function createTranslator(language, catalogs, onMissing) {
    const primary = catalogs[language] ?? {};
    const fallback = catalogs[DEFAULT_LANGUAGE] ?? {};
    return (key, vars) => {
        const hit = primary[key] ?? fallback[key];
        if (hit === undefined) {
            onMissing?.(key, language);
            return key;
        }
        return interpolate(hit, vars);
    };
}
/**
 * Las claves que le faltan a un idioma respecto al de referencia.
 *
 * Es lo que hace gobernable una traducción a medias: sin esto, «¿qué queda?» se
 * contesta mirando pantallas una por una. Se usa en una prueba, para que una
 * clave nueva sin traducir salga en CI y no en producción.
 */
export function missingKeys(catalogs, language, reference = DEFAULT_LANGUAGE) {
    const target = catalogs[language] ?? {};
    return Object.keys(catalogs[reference] ?? {}).filter((key) => target[key] === undefined);
}
/** Al revés: claves que sobran en un idioma y ya no existen en el de referencia. */
export function orphanKeys(catalogs, language, reference = DEFAULT_LANGUAGE) {
    const source = catalogs[reference] ?? {};
    return Object.keys(catalogs[language] ?? {}).filter((key) => source[key] === undefined);
}
//# sourceMappingURL=translator.js.map